import React, { useMemo, useEffect, useCallback } from 'react'

import {
  usePRFileArtboards,
  usePan,
  useRootNodeAbsoluteExtent,
} from '@sketch-hq/sketch-web-renderer'
import { AnnotationDotFragment } from '@sketch/gql-types'

import AnnotationOverlay from '../AnnotationOverlay'
import { ReferenceBounds } from '../../types'
import { usePlaceDraftAnnotation } from '../../hooks'

interface PageAnnotationsOverlayProps {
  shareIdentifier: string
  permanentPageId: string
  parentWrapper?: HTMLElement
  activeAnnotationIdentifier?: string
}

const DEFAULT_PAN = { x: 0, y: 0 }

const throttleUpdatePopover = () => {
  window.requestAnimationFrame(() => {
    /**
     * This is a work-around to force popper to update
     * its position.
     *
     * Because popper has listeners for scroll and window
     * resize, we send this fake event when the camera is moving
     *
     * The other way would be using refs and would make the
     * logic complex
     */
    window?.dispatchEvent(new CustomEvent('scroll'))
  })
}

const PageAnnotationsOverlay = (props: PageAnnotationsOverlayProps) => {
  const { parentWrapper, activeAnnotationIdentifier } = props

  const artboards = usePRFileArtboards()

  const [isPlacingDraftAnnotation] = usePlaceDraftAnnotation() || []
  const rootExtent = useRootNodeAbsoluteExtent()

  const artboardCoordinatesByUuid = useMemo(() => {
    const coordinatesByUuid: Record<string, ReferenceBounds> = {}

    artboards?.forEach(artboard => {
      coordinatesByUuid[artboard.objectId] = {
        x: artboard.bounds.x,
        y: artboard.bounds.y,
        height: artboard.bounds.height,
        width: artboard.bounds.width,
      }
    })

    return coordinatesByUuid
  }, [artboards])

  const getPrefixCoordinates = useCallback(
    (annotation: AnnotationDotFragment) => {
      if (annotation.currentSubject.__typename === 'Artboard') {
        return artboardCoordinatesByUuid[annotation.currentSubject.uuid]
      }
    },
    [artboardCoordinatesByUuid]
  )

  /**
   * When the user is panning on the canvas he might reach any
   * of the borders, if possible the popover should move to the
   * opposite side until reaching the other edge.
   */
  const pan = usePan() || DEFAULT_PAN
  useEffect(() => {
    /**
     * We should only activate this when the camera is moving
     * because when zooming we already have the "DROPDOWN_MODIFIERS"
     * that should keep the popover close to the dot
     */
    if (!(activeAnnotationIdentifier || isPlacingDraftAnnotation)) {
      return
    }

    throttleUpdatePopover()
  }, [pan.x, pan.y, activeAnnotationIdentifier, isPlacingDraftAnnotation])

  return (
    <AnnotationOverlay
      referenceBounds={rootExtent}
      getPrefixCoordinates={getPrefixCoordinates}
      getArtboardBounds={getPrefixCoordinates}
      parentWrapper={parentWrapper}
      {...props}
    />
  )
}

export default PageAnnotationsOverlay
